import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import { getCalendarAvailability } from '~/modules-rocco/booking/api/get-calendar-availability.server';
import type { CalendarAvailabilityData } from '~/modules-rocco/booking/api/get-calendar-availability.server';

export const BOOKING_AVAILABILITY_API_ROUTE = '/remix-api/booking/availability';

/** Availability window for the inline project-detail booking card. */
const INLINE_CARD_WEEKS_AHEAD = 2;

interface BookingAvailabilityError {
    code: 'notFound' | 'loadFailed' | 'sessionDisabled' | 'noConsultant';
    message: string;
}

export interface BookingAvailabilityApiResponse {
    availability?: CalendarAvailabilityData;
    error?: BookingAvailabilityError;
}

const QUERY_SCHEMA = z.object({
    sessionId: z.coerce.number().int().positive(),
    timezone: z.string().min(1).optional(),
});

/**
 * Shrinks the availability payload for callers that render no timezone selector
 * (the inline project-detail booking card). The backend returns ~3.6MB: the full
 * day/slot grid for every supported timezone (~11 copies differing only in how
 * absolute timestamps group into local dates), and every slot repeats each
 * available member's name + email. Two cuts, ~21× smaller:
 *
 *  1. Keep one timezone — the requested one, else the first. The client derives
 *     any other from slot timestamps via `convertFreeDaysToTimezone`.
 *  2. Keep `memberId` only on each slot. names come from the top-level
 *     `teamMembers` roster; email is never used client-side. This alone is ~2/3
 *     of a single timezone's bytes.
 */
const leanSingleTimezone = (
    freeDaysByDuration: CalendarAvailabilityData['freeDaysByDuration'],
    requestedTimezone: string | undefined,
): CalendarAvailabilityData['freeDaysByDuration'] =>
    freeDaysByDuration.map((durationGroup) => {
        const groups = durationGroup.freeDays;
        const match = requestedTimezone
            ? groups.find((g) => g.timezone === requestedTimezone)
            : undefined;
        const base = match ?? groups[0];
        if (!base) return durationGroup;
        return {
            duration: durationGroup.duration,
            freeDays: [
                {
                    timezone: base.timezone,
                    days: base.days.map((day) => ({
                        date: day.date,
                        freeSlots: day.freeSlots.map((slot) => ({
                            label: slot.label,
                            timestamp: slot.timestamp,
                            availableMembers: slot.availableMembers.map(
                                (m) => ({
                                    memberId: m.memberId,
                                }),
                            ),
                        })),
                    })),
                },
            ],
        };
    });

/**
 * Client-side data source for the in-page booking modal. Wraps the same
 * {@link getCalendarAvailability} the /booking page loader uses and applies the
 * identical active + connected-member filtering, so the modal renders exactly
 * what the standalone booking page would for a given session.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse({
        sessionId: url.searchParams.get('sessionId'),
        timezone: url.searchParams.get('timezone') ?? undefined,
    });
    // The inline project-detail booking card opts in: it has no timezone
    // selector, so it only needs one timezone's slots. The Client Director modal
    // omits this and keeps every timezone for its dropdown.
    const singleTimezone = url.searchParams.get('singleTimezone') === '1';

    if (!parsed.success) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'notFound',
                    message: 'A valid booking session id is required.',
                },
            },
            { status: 400 },
        );
    }

    const { sessionId, timezone } = parsed.data;
    // The inline card shows a compact calendar and doesn't need the full default
    // window, so it asks the backend for a shorter range — this shrinks the slow
    // per-member calendar fetch. Other callers (modal) keep the default.
    const weeksAhead = singleTimezone ? INLINE_CARD_WEEKS_AHEAD : undefined;
    const result = await getCalendarAvailability(
        sessionId,
        context,
        timezone,
        undefined,
        weeksAhead,
        // The same flag the trim below reads. It travels to the backend now as
        // well, so the ten zones this caller never renders are not built and
        // shipped here only to be dropped a few lines down.
        singleTimezone,
    );

    if (!result.success) {
        return routerData<BookingAvailabilityApiResponse>(
            { error: result.error },
            { status: result.error.code === 'notFound' ? 404 : 400 },
        );
    }

    const availability = result.data;

    if (!availability.isActive) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'sessionDisabled',
                    message: 'This booking session is not currently available.',
                },
            },
            { status: 403 },
        );
    }

    // Drop team members without a usable calendar connection (mirrors the page loader).
    const connectedTeamMembers =
        availability.teamMembers?.filter((member) => {
            const connections = member.CalendarConnection || [];
            return connections.some((c) => Boolean(c.provider));
        }) || [];

    const hasConnectedOneOnOneMember =
        availability.meetingType === 'one-on-one' &&
        availability.oneOnOneTeamMember?.CalendarConnection?.some((c) =>
            Boolean(c.provider),
        );

    const hasConnectedMembers =
        availability.meetingType === 'one-on-one'
            ? hasConnectedOneOnOneMember
            : connectedTeamMembers.length > 0;

    if (!hasConnectedMembers) {
        return routerData<BookingAvailabilityApiResponse>(
            {
                error: {
                    code: 'noConsultant',
                    message:
                        'No team members are currently available for booking.',
                },
            },
            { status: 409 },
        );
    }

    if (availability.meetingType !== 'one-on-one') {
        availability.teamMembers = connectedTeamMembers;
    }

    if (singleTimezone) {
        availability.freeDaysByDuration = leanSingleTimezone(
            availability.freeDaysByDuration,
            timezone,
        );
    }

    return routerData<BookingAvailabilityApiResponse>({ availability });
};
